<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { LockIcon } from "@lucide/vue"

import type { TimeBlock } from "@/entities/time-block"
import { bookingTop, bookingHeight, minutesOfDayInTz } from "../model/timeGrid"
import { useRecordsStore } from "../model/store"

interface Props {
  block: TimeBlock
  gridStartMin: number
  tz: string
}

const props = defineProps<Props>()
const store = useRecordsStore()
const { t } = useI18n()

/** Absolute top offset (px) inside the grid column. */
const topPx = computed(() =>
  bookingTop({ startsAt: props.block.startsAt, endsAt: props.block.endsAt }, props.gridStartMin, props.tz),
)

/** Height (px) — min 44px for ≥44px tap target. */
const heightPx = computed(() =>
  bookingHeight({ startsAt: props.block.startsAt, endsAt: props.block.endsAt }, props.tz, 44),
)

/** Format a UTC ISO instant as HH:MM in the given tz. */
function formatHHMM(iso: string, tz: string): string {
  const minOfDay = minutesOfDayInTz(iso, tz)
  const h = Math.floor(minOfDay / 60)
  const m = minOfDay % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const timeRange = computed(
  () =>
    `${formatHHMM(props.block.startsAt, props.tz)}–${formatHHMM(props.block.endsAt, props.tz)}`,
)

const reasonDisplay = computed(() =>
  props.block.reason.length > 0 ? props.block.reason : t("journal.timeBlock.busy"),
)

const ariaLabel = computed(() => {
  const parts = [`Блок, ${timeRange.value}`]
  if (props.block.reason.length > 0) {
    parts.push(props.block.reason)
  }
  return parts.join(", ")
})

function handleClick(): void {
  store.openDeleteBlock(props.block)
}
</script>

<template>
  <button
    class="block-card"
    :data-testid="`block-card-${block.id}`"
    :style="{ top: `${topPx}px`, height: `${heightPx}px` }"
    :aria-label="ariaLabel"
    type="button"
    @click.stop="handleClick"
  >
    <LockIcon
      class="block-card__icon"
      aria-hidden="true"
    />
    <span class="block-card__reason">{{ reasonDisplay }}</span>
  </button>
</template>

<style scoped>
.block-card {
  position: absolute;
  left: 2px;
  right: 2px;
  z-index: 2;
  border-radius: 4px;
  padding: 4px 6px;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  border: none;
  color: var(--muted-foreground);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;

  /* Neutral diagonal-hatch fill — no category colour */
  background-color: var(--muted);
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 4px,
    color-mix(in oklch, var(--muted-foreground) 15%, transparent) 4px,
    color-mix(in oklch, var(--muted-foreground) 15%, transparent) 5px
  );

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
}

.block-card__icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  margin-top: 1px;
}

.block-card__reason {
  font-size: 0.7rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
</style>
